import { useEffect, useState, type FC, type ReactNode } from "react";
import { Button, Chip, Input, Popover, TextField } from "@heroui/react";

type ChipColor = "warning" | "accent" | "success" | "default";

export interface PickerOption<T extends string> {
  value: T;
  label: string;
  color?: ChipColor;
}

interface HelpChipPickerPopoverProps<T extends string> {
  ariaLabel: string;
  value: T;
  options: PickerOption<T>[];
  onChange: (value: T) => void;
  placement?: "bottom start" | "bottom end";
  children: ReactNode;
}

export function HelpChipPickerPopover<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
  placement = "bottom start",
  children,
}: HelpChipPickerPopoverProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger>
        <button type="button" className="help-chip-button" aria-label={ariaLabel}>
          {children}
        </button>
      </Popover.Trigger>
      <Popover.Content placement={placement} className="help-chip-popover">
        <Popover.Dialog className="help-chip-popover__menu">
          {options.map((option) => {
            const isActive = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                className={
                  isActive
                    ? "help-chip-popover__item help-chip-popover__item--active"
                    : "help-chip-popover__item"
                }
                onMouseDown={(event) => {
                  event.preventDefault();
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                <Chip size="sm" variant="soft" color={option.color ?? "default"}>
                  {option.label}
                </Chip>
              </button>
            );
          })}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

interface HelpEtaChipPopoverProps {
  value: number | null;
  onSave: (minutes: number | null) => void;
}

const ETA_PRESETS = [5, 10, 15, 20, 30, 45, 60];

export const HelpEtaChipPopover: FC<HelpEtaChipPopoverProps> = ({ value, onSave }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setDraft(value == null ? "" : String(value));
  }, [isOpen, value]);

  const commit = (minutes: number | null) => {
    onSave(minutes);
    setIsOpen(false);
  };

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      commit(null);
      return;
    }
    commit(Math.max(0, Number(trimmed) || 0));
  };

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger>
        <button
          type="button"
          className="help-chip-button"
          aria-label="Изменить время ожидания исполнителя"
        >
          {value != null ? (
            <Chip size="sm" variant="soft" color="accent">
              ~{value} мин
            </Chip>
          ) : (
            <Chip size="sm" variant="soft">
              Время не назначено
            </Chip>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Content placement="bottom end" className="help-chip-popover help-chip-popover--eta">
        <Popover.Dialog className="help-chip-popover__eta">
          <span className="help-chip-popover__title">Время ожидания</span>
          <label className="help-form-field">
            Минут до прибытия
            <TextField fullWidth>
              <Input
                inputMode="numeric"
                placeholder="Не назначено"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitDraft();
                  }
                }}
              />
            </TextField>
          </label>
          <div className="help-eta-presets">
            {ETA_PRESETS.map((minutes) => (
              <Button
                key={minutes}
                size="sm"
                variant={value === minutes ? "primary" : "tertiary"}
                onPress={() => commit(minutes)}
              >
                {minutes} мин
              </Button>
            ))}
          </div>
          <div className="help-chip-popover__actions">
            <Button size="sm" variant="tertiary" onPress={() => commit(null)}>
              Не назначено
            </Button>
            <Button size="sm" variant="primary" onPress={commitDraft}>
              Сохранить
            </Button>
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
};
